import React, {useEffect, useRef, useState} from 'react'
import {KTSVG} from '../../../../helpers'
import {SearchType} from '../../../../../app/pages/search/types'
import {nextActiveIndex} from '../../../../../app/pages/search/utils'
import {iconForType, labelForType, SEARCH_TYPE_META} from './searchTypeMeta'

interface TypeSelectorProps {
  value: SearchType
  onChange: (type: SearchType) => void
  disabled?: boolean
}

const MENU_ID = 'nb-gs-type-menu'

/**
 * Type selector combobox (GS-01) with a11y: [icon] [label] ▾. The trigger is a button with
 * aria-haspopup/aria-expanded that announces the selected type (aria-label). Open with click or
 * arrow/Enter; arrow keys move the highlighted option (aria-activedescendant), Enter selects,
 * Escape closes and returns focus to the trigger. Click-outside also closes. Focus is never trapped.
 */
export const TypeSelector: React.FC<TypeSelectorProps> = ({value, onChange, disabled}) => {
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const currentIndex = Math.max(0, SEARCH_TYPE_META.findIndex((m) => m.value === value))

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  const openMenu = () => {
    setActiveIdx(currentIndex)
    setOpen(true)
  }

  const close = (refocus = true) => {
    setOpen(false)
    if (refocus) triggerRef.current?.focus()
  }

  const select = (type: SearchType) => {
    onChange(type)
    close()
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        openMenu()
        e.preventDefault()
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
        setActiveIdx((i) => nextActiveIndex(i, e.key, SEARCH_TYPE_META.length))
        e.preventDefault()
        break
      case 'Enter':
      case ' ':
        select(SEARCH_TYPE_META[activeIdx].value)
        e.preventDefault()
        break
      case 'Escape':
        close()
        e.preventDefault()
        break
      case 'Tab':
        setOpen(false) // let focus leave naturally — never trap
        break
      default:
        break
    }
  }

  return (
    <div className='nb-gs__type' ref={rootRef}>
      <button
        ref={triggerRef}
        type='button'
        className='nb-gs__type-trigger'
        disabled={disabled}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-controls={open ? MENU_ID : undefined}
        aria-label={`Search type: ${labelForType(value)}`}
        aria-activedescendant={open ? `${MENU_ID}-opt-${activeIdx}` : undefined}
        onClick={() => {
          if (disabled) return
          open ? setOpen(false) : openMenu()
        }}
        onKeyDown={(e) => {
          if (disabled) return
          onKeyDown(e)
        }}
        style={disabled ? {cursor: 'not-allowed', opacity: 0.6} : undefined}
      >
        <KTSVG path={iconForType(value)} className='svg-icon-3 nb-gs__type-icon' />
        <span className='nb-gs__type-label'>{labelForType(value)}</span>
        <KTSVG path='/media/icons/duotune/arrows/arr072.svg' className='svg-icon-5 nb-gs__type-caret' />
      </button>

      {open && (
        <div className='nb-gs__type-menu' role='listbox' id={MENU_ID} aria-label='Search type'>
          {SEARCH_TYPE_META.map((m, i) => (
            <button
              key={m.value}
              type='button'
              role='option'
              id={`${MENU_ID}-opt-${i}`}
              aria-selected={m.value === value}
              className={`nb-gs__type-option${i === activeIdx ? ' nb-gs__type-option--active' : ''}${
                m.value === value ? ' nb-gs__type-option--selected' : ''
              }`}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseDown={(e) => e.preventDefault()} // keep focus on the trigger
              onClick={() => select(m.value)}
            >
              <KTSVG path={m.icon} className='svg-icon-3 nb-gs__type-option-icon' />
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
